import { Decimal } from 'decimal.js-light';
import { Temporal } from 'temporal-polyfill';
import { isNil } from './fn';
import { isTemporal } from './temporal';

type TemporalType =
	| typeof Temporal.PlainDate
	| typeof Temporal.PlainTime
	| typeof Temporal.PlainDateTime
	| typeof Temporal.ZonedDateTime
	| typeof Temporal.Instant;

type TemporalCompare = (a: unknown, b: unknown) => number;

export function compareNil(a: unknown, b: unknown): number | null {
	if (isNil(a) && isNil(b)) return 0;
	if (!isNil(a) && isNil(b)) return -1;
	if (isNil(a) && !isNil(b)) return 1;
	return null;
}

export function comparePlain(a: unknown, b: unknown): number {
	if (a! < b!) return -1;
	if (a! > b!) return 1;
	return 0;
}

function toDecimalOrNull(value: unknown): Decimal | null {
	if (typeof value === 'number') return Number.isNaN(value) ? null : new Decimal(value);
	if (typeof value !== 'string' || value.trim() === '') return null;

	try {
		return new Decimal(value);
	}
	catch {
		return null;
	}
}

/**
 * Compares two values numerically, or null when either isn't a finite number or a
 * numeric string. Numbers compare natively; string-encoded numbers (int64/decimal,
 * kept as strings to preserve precision) compare via Decimal so "100" > "9".
 */
export function compareNumericOrNull(a: unknown, b: unknown): number | null {
	if (typeof a === 'number' && typeof b === 'number') {
		if (Number.isNaN(a) || Number.isNaN(b)) return null;
		return a < b ? -1 : (a > b ? 1 : 0);
	}

	const decimalA = toDecimalOrNull(a);
	const decimalB = toDecimalOrNull(b);
	return decimalA && decimalB ? decimalA.cmp(decimalB) : null;
}

function toInstant(value: unknown): Temporal.Instant | null {
	if (value instanceof Temporal.Instant) return value;
	if (value instanceof Temporal.ZonedDateTime) return value.toInstant();
	return null;
}

/**
 * Compares two Temporal values by their intrinsic ordering, or null when they
 * aren't the same kind. Zoned/Instant values compare by absolute moment; plain
 * values compare within their own type.
 */
export function compareTemporalMoment(a: unknown, b: unknown): number | null {
	const instantA = toInstant(a);
	const instantB = toInstant(b);

	if (instantA && instantB) return Temporal.Instant.compare(instantA, instantB);
	if (a instanceof Temporal.PlainDateTime && b instanceof Temporal.PlainDateTime) return Temporal.PlainDateTime.compare(a, b);
	if (a instanceof Temporal.PlainDate && b instanceof Temporal.PlainDate) return Temporal.PlainDate.compare(a, b);
	if (a instanceof Temporal.PlainTime && b instanceof Temporal.PlainTime) return Temporal.PlainTime.compare(a, b);
	return null;
}

export function compareTemporal(type: TemporalType, a: unknown, b: unknown): number {
	if (a instanceof type && b instanceof type) {
		return (type.compare as TemporalCompare)(a, b);
	}

	return comparePlain(a, b);
}

/**
 * Compares two values for sorting. Returns -1 if a < b, 1 if a > b, 0 if equal.
 * Null/undefined are ordered last for ascending (treated as greater than any value).
 * Temporal values are compared as Temporal objects via their type's compare method.
 *
 * Value-based: probes each value's type at comparison time. Prefer a
 * schema-driven comparator (see `sort-items`) when the field type is known.
 */
export function compareValues(a: unknown, b: unknown): number {
	const nilResult = compareNil(a, b);
	if (nilResult !== null) return nilResult;

	if (isTemporal(a) && isTemporal(b)) {
		return compareTemporalMoment(a, b) ?? 0;
	}

	return comparePlain(a, b);
}
